import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Button,
  Card,
  CardContent,
  IconButton,
  TextField,
  Box,
  Typography,
  useMediaQuery,
  createTheme,
} from "@mui/material";
import {
  Chat as ChatIcon,
  Close as CloseIcon,
  Minimize as MinimizeIcon,
  OpenInFull,
  Send as SendIcon,
} from "@mui/icons-material";
import { autoReplies } from "./autoRepliesData";

const TNPCBChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    { text: autoReplies.hello, sender: "bot" },
  ]);
  const [newMessage, setNewMessage] = useState("");
  const theme = createTheme();
  const isSm = useMediaQuery(() => theme.breakpoints.down("sm"));

  const messagesEndRef = useRef(null);

  const findBestMatch = useCallback((input) => {
    const words = input.toLowerCase().split(" ");
    const directMatch = autoReplies[input.toLowerCase()];

    if (directMatch) {
      return directMatch;
    }
    const topics = Object.keys(autoReplies);
    const matches = topics.filter((topic) =>
      words.some((word) => word.includes(topic) || topic.includes(word))
    );

    if (matches.length > 0) {
      const bestMatch = matches.reduce((a, b) => (a.length > b.length ? a : b));
      return autoReplies[bestMatch];
    }
    return "I'm here to help with questions about TNPCB, pollution control, and environmental regulations. Could you please be more specific about your inquiry?";
  }, []);

  const handleSend = () => {
    if (newMessage.trim()) {
      const userMessage = { text: newMessage, sender: "user" };
      setMessages((prev) => [...prev, userMessage]);

      const botResponse = findBestMatch(newMessage);
      setTimeout(() => {
        setMessages((prev) => [...prev, { text: botResponse, sender: "bot" }]);
      }, 500);

      setNewMessage("");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isOpen]);

  if (!isOpen) {
    return (
      <Button
        variant="contained"
        color="primary"
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
          minWidth: "auto",
          width: 56,
          height: 56,
          borderRadius: "50%",
          animation: "blink 1s infinite",
          "@keyframes blink": {
            "0%, 100%": { transform: "scale(1)" },
            "50%": { transform: "scale(1.2)" },
          },
        }}
        onClick={() => {
          setIsOpen(true);
          setIsMinimized(false);
        }}
      >
        <ChatIcon />
      </Button>
    );
  }

  return (
    <Card
      sx={{
        position: "fixed",
        bottom: 16,
        right: !isSm ? 16 : 5,
        width: isSm ? 300 : 380,
        height: isMinimized ? 48 : 500,
        transition: "height 0.3s ease",
        overflow: "hidden",
        boxShadow: 3,
      }}
    >
      <Box
        sx={{
          bgcolor: "primary.main",
          color: "primary.contrastText",
          p: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ChatIcon fontSize="small" />
          <Typography variant="subtitle1">TNPCB Support</Typography>
        </Box>
        <Box>
          <IconButton
            size="small"
            onClick={() => setIsMinimized(!isMinimized)}
            sx={{ color: "inherit", mr: 1 }}
          >
            {!isMinimized ? (
              <MinimizeIcon fontSize="small" />
            ) : (
              <OpenInFull fontSize="small" />
            )}
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setIsOpen(false)}
            sx={{ color: "inherit" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {!isMinimized && (
        <CardContent
          sx={{
            p: "0 !important",
            height: "calc(100% - 48px)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {messages.map((message, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  justifyContent:
                    message.sender === "user" ? "flex-end" : "flex-start",
                }}
              >
                <Box
                  sx={{
                    maxWidth: "80%",
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor:
                      message.sender === "user" ? "primary.main" : "grey.100",
                    color:
                      message.sender === "user"
                        ? "primary.contrastText"
                        : "text.primary",
                  }}
                >
                  <Typography variant="body2">{message.text}</Typography>
                </Box>
              </Box>
            ))}
            <div ref={messagesEndRef} />
          </Box>

          <Box
            sx={{
              borderTop: 1,
              borderColor: "divider",
              p: 2,
              display: "flex",
              gap: 1,
            }}
          >
            <TextField
              fullWidth
              multiline
              maxRows={4}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about pollution control, environmental regulations..."
              size="small"
              sx={{ flex: 1 }}
            />
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={!newMessage.trim()}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </CardContent>
      )}
    </Card>
  );
};

export default TNPCBChatbot;
